import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import brightcovePlayerLoader from '@brightcove/player-loader';
import {select, Store} from '@ngrx/store';
import {get} from 'lodash';
import {Subscription} from 'rxjs';
import {filter, take, tap} from 'rxjs/operators';
import { FacebookPixelEventTrackerService } from 'src/app/facebook-pixel-event-tracker.service';
import {CourseLecture} from '../../services/course-data.service';
import {State} from '../../state';
import * as courseSelectors from '../../state/courses/selectors';
import * as ui from '../../state/ui';
import {VideoComponent} from '../../videoComponent';

//globally included SoundCloud API code
declare var SC;

@Component({
	selector: 'app-lecture',
	templateUrl: './lecture.component.html',
	styleUrls: ['./lecture.component.less']
})
export class LectureComponent extends VideoComponent implements OnInit, OnChanges, OnDestroy {

	@Input() courseId: string;
	@Input() lecture: CourseLecture = null;
	@Input() layout = 'main';
	@Input() showVideo = true;

	playerControlsDisabled = false;
	showDownloadNotice = false;

	currentSelection = 'lecture';

	public soundcloudAudioSrc;
	public soundcloudDownloadSrc;

	private curLocationLookupSub: Subscription;
	private modalWatcherSub: Subscription;

	@ViewChild('lectureVideo', { static: false })
	get lectureVideoRef(): ElementRef { return this.playerVideoRef; }
	set lectureVideoRef(value: ElementRef) { this.playerVideoRef = value; }

	@ViewChild('soundCloud', {static: false})
	soundCloudIframeRef: ElementRef;

	soundCloudWidget: any;
	soundCloudLoadHandle: NodeJS.Timer;

	constructor(
		public domSanitizer: DomSanitizer,
		store: Store<State>,
		facebookPixelEventTracker: FacebookPixelEventTrackerService,
	) {
		super(store, facebookPixelEventTracker);
	}

	ngOnInit() {
		this.curLocationLookupSub = this.store.pipe(
			select(courseSelectors.getVideoLoc(), {videoId: this.lecture.video_id}),
			filter(i => i),
			tap(i => console.log('video-loc', i)),
			take(1)
		).subscribe(position => {
			this.videoStartPosition = position.position;
			this.setPositionIfNotStartedAndNotAtEndOfVideo();
		});
	}

	ngOnDestroy() {
		if (this.curLocationLookupSub && !this.curLocationLookupSub.closed) {
			this.curLocationLookupSub.unsubscribe();
		}

		if (this.modalWatcherSub && !this.modalWatcherSub.closed) {
			this.modalWatcherSub.unsubscribe();
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		// console.log('change', changes, this.lecture, this.showVideo);
		if (changes.lecture || changes.showVideo) {
			const videoIdIsSame = get(changes, 'lecture.currentValue.video_id') === get(changes, 'lecture.previousValue.video_id');
			const showVideoIsSame = get(changes, 'showVideo.currentValue') === get(changes, 'showVideo.previousValue');
			if (videoIdIsSame && showVideoIsSame) {
				console.log('"change" to lecture, but the same video');
				return;
			}

			if (this.lecture && this.showVideo) {
				// console.log('got lecture', changes.lecture.currentValue, this.showVideo);
				this.clearPreviousVideoPlayer();
				this.loadVideo(this.lecture.video_id, true, this.lecture.system_id);
			} else if (!this.showVideo && !showVideoIsSame) {
				if (this.player) {
					this.player.pause();
					this.clearPreviousVideoPlayer();
				}

				//stop watching for modal changes
				if (this.modalWatcherSub != null && !this.modalWatcherSub.closed) {
					this.modalWatcherSub.unsubscribe();
				}
			}
		}
	}

	onPlayerInit() {
		this.setPositionIfNotStartedAndNotAtEndOfVideo();

		this.modalWatcherSub = this.store.pipe(select(ui.selectModalStatus)).subscribe(modalOpen => {
			if (!this.player) return;

			//if the player is playing and a modal just opened, pause the video
			if (!this.player.paused() && modalOpen) {
				console.log('paused the video');
				this.player.pause();
			}
		});
	}

	private setPositionIfNotStartedAndNotAtEndOfVideo() {
		if (!this.player || this.videoStarted || !this.videoStartPosition) return;

		const duration = this.getVideoDuration();
		console.log('last position', this.videoStartPosition, '; video duration', duration);

		if (!duration || duration.duration - 30 > this.videoStartPosition) {
			this.player.currentTime(this.videoStartPosition);

			//if the soundcloud widget has loaded, then set its start time too
			if (this.soundCloudWidget) {
				this.soundCloudWidget.seekTo(this.videoStartPosition * 1000);
			}
		} else {
			console.log('video is completed. Not setting position');
		}
	}

	private getVideoDuration() {
		const playerDur = this.player && this.player.duration();
		if (playerDur) return {type: 'player', duration: playerDur};

		//could not get duration from player (https://docs.videojs.com/player#duration)
		//lets try parsing the "duration" property on the lecture
		const parts = this.lecture && this.lecture.duration && this.lecture.duration.split(':');
		if (parts && parts.length === 2) {
			const minutes = parseInt(parts[0], 10);
			const seconds = parseInt(parts[1], 10);

			//if we got valid numbers from both parts, just convert the minutes and seconds to seconds and return
			if (!Number.isNaN(seconds) && !Number.isNaN(minutes)) {
				return {type: 'lecture', duration: 60 * minutes + seconds};
			}
		}

		//unable to find duration. Return nothing
		return null;
	}

	parseVideoIDIntoSrc(videoID) {
		let parsedID: SafeResourceUrl;
		if (videoID) {
			videoID = videoID.toString();
			if (videoID.includes('/')) {
				if (videoID.includes('showcase')) {
					parsedID = this.domSanitizer.bypassSecurityTrustResourceUrl('https://vimeo.com/' + videoID + '/embed');
				} else {
					parsedID = this.domSanitizer.bypassSecurityTrustResourceUrl('https://player.vimeo.com/video/' + videoID.split('/')[0] + '?h=' + videoID.split('/')[1]);
				}
			} else {
				parsedID = this.domSanitizer.bypassSecurityTrustResourceUrl('https://player.vimeo.com/video/' + videoID);
			}
		}
		return parsedID;
	}
}
